// @ts-strict-ignore
import interpolateComponents from '@automattic/interpolate-components';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { sprintf } from 'sprintf-js';

import { SignUpBlockData } from 'api/cms';
import { PublicPage } from 'api/website';
import { Box } from 'components/Box';
import { PrimaryButtonLink } from 'components/Button';
import EmailSignupFormContainer from 'containers/EmailSignupFormContainer';
import LoginFormContainer from 'containers/LoginFormContainer';
import { useCurrentUser } from 'store/main/hooks';
import { gutterWidth, maxWidth } from 'theme';
import { trackEvent } from 'utils/analytics';
import { getFirstParam } from 'utils/url';
import { useTemporaryStorage } from 'utils/useTemporaryStorage';

import { SignUpBlockContainer } from './SignUpBlockContainer';
import { SignUpBlockHeadline } from './SignUpBlockHeadline';

type Props = {
  data: SignUpBlockData;
  isPageHeader: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-prop-types
  content: PublicPage;
};

export function SignUpBlock({
  data: {
    headline,
    redirectUrl,
    bgImage,
    loggedInHeadline,
    loggedInRedirectButtonText,
  },
  isPageHeader,
}: Props) {
  const currentUser = useCurrentUser();
  const location = useLocation();
  const tmpId = getFirstParam(location, 'tmpId');
  const [saveTemporaryStorage] = useTemporaryStorage<{
    redirectUrl: string;
  }>(currentUser.id && currentUser.emailVerified ? tmpId : '');
  const [redirectTo, setRedirectTo] = useState(null);
  const [saved, setSaved] = useState(false);
  const [emailInUse, setEmailInUse] = useState('');
  useEffect(() => {
    trackEvent('View Sign Up Form');
  }, []);
  useEffect(() => {
    if (!redirectTo && !tmpId && !saved) {
      setSaved(true);
      saveTemporaryStorage({
        SIGNUP_BLOCK_REDIRECT: {
          redirectUrl,
        },
      }).then((id) => {
        setRedirectTo(
          `${window.location.pathname}${window.location.search}${
            window.location.search ? '&' : '?'
          }tmpId=${id}`
        );
      });
    }
  }, [redirectTo, redirectUrl, saveTemporaryStorage, tmpId, saved]);

  const useLoggedInUi = currentUser.id && loggedInHeadline && !emailInUse;

  return (
    <SignUpBlockContainer data-qa-id="sign-up">
      <SignUpBlockContainer
        imageHandle={bgImage ? bgImage.handle : null}
        mimetype={bgImage ? bgImage.mimetype : null}
      >
        <Box
          mx="auto"
          p={[0, null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <Box display="flex" justifyContent="flex-end">
            <Box
              width={[1, null, 0.5]}
              p={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[0, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              <SignUpBlockHeadline
                text={useLoggedInUi ? loggedInHeadline : headline}
                isPageHeader={isPageHeader}
              />

              <Box mt="32px">
                {useLoggedInUi && (
                  <>
                    {interpolateComponents({
                      mixedString: sprintf(
                        getText(
                          'You are logged in as {{strong}}%(email)s{{/strong}}'
                        ),
                        {
                          email: currentUser.email,
                        }
                      ),
                      components: {
                        strong: <strong />,
                      },
                    })}
                    {redirectUrl && loggedInRedirectButtonText && (
                      <Box mt={24}>
                        <PrimaryButtonLink href={redirectUrl}>
                          {loggedInRedirectButtonText}
                        </PrimaryButtonLink>
                      </Box>
                    )}
                  </>
                )}
                {!useLoggedInUi && (
                  <>
                    {!emailInUse && (
                      <EmailSignupFormContainer
                        isModal={false}
                        redirectTo={redirectTo}
                        savedListing={null}
                        subscribedSubsite={null}
                        onLogin={null}
                        onSignup={null}
                        compactView
                        hideLoginBlock
                        customButtonText={getText('Continue')}
                        setEmailInUse={setEmailInUse}
                      />
                    )}
                    {emailInUse && (
                      <LoginFormContainer
                        isModal={false}
                        redirectTo={redirectTo}
                        email={emailInUse}
                        onLogin={null}
                        header=""
                        compactView
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </SignUpBlockContainer>
    </SignUpBlockContainer>
  );
}
